import { useState } from 'react';
import { Plus } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import { cn } from 'lib/utils';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { CONFIG_SECTIONS } from './agent-configuration.constants';
import { AgentConfigAddToolModal } from './agent-config-add-tool-modal';
import { AgentConfigAddSkillModal } from './agent-config-add-skill-modal';
import { AgentConfigAddSubagentModal } from './agent-config-add-subagent-modal';
import { AgentConfigAddKBModal } from './knowledge-base-section/agents-config-add-kb-modal';
import { KnowledgeBaseSection } from './knowledge-base-section';
import { LlmConfigurationSection } from './llm-configuration-section-panel';
import { MemorySection } from './memory-section';
// import { SafetyMechanismSection } from './safety-mechanism-section';
import { GuardrailsSection } from './guardrails-section';
import { AgentConfigAddGuardrailModal } from './agent-config-add-guardrail-modal';
import { ToolsSection } from './tools-section';
import { SkillsSection } from './skills-section';
import { SubagentsSection } from './subagents-section';
import { WelcomeGuideSection } from './welcome-guide-section';
import type {
  IGetAgentModelResponse,
  IGetCustomLlmModelsResponse,
  ITool,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import type {
  Agent,
  AgentSummary,
  CustomRule,
  GuardrailCatalogItem,
  GuardrailConfig,
  GuardrailOutputStrategy,
} from 'features/chat/agent-chat-module/types/agent.service.type';
import type { SkillListItem } from 'pages/skills/skills.types';
import {
  canEditAgentConfig,
  canManageKb,
  canManageSkills,
  canManageSubagents,
  canManageTools,
  isAgentOwnerAccess,
} from '../utils/agent-share-access';

const figmaConfigSwitchClassName =
  'h-5 w-9 border-transparent bg-grey-100 data-[state=checked]:bg-black-base dark:!bg-muted dark:data-[state=checked]:!bg-true-white [&_[data-state]]:size-4 [&_[data-state=checked]]:translate-x-[16px] [&_[data-state=unchecked]]:translate-x-0.5 [&>span]:!bg-true-white dark:[&>span]:!bg-true-white dark:data-[state=checked]:[&>span]:!bg-black-base';

const figmaAddButtonClassName =
  'h-[38px] shrink-0 gap-[6px] rounded-[62px] border border-grey-100 bg-grey-50 pl-[18px] pr-6 text-[16px] font-medium leading-[1.2] text-[#3d3d3d] shadow-none hover:bg-[#f0f0f0] hover:text-[#3d3d3d] dark:!border-grey-100 dark:!bg-grey-50 dark:!text-[#3d3d3d] dark:hover:!bg-[#f0f0f0] dark:hover:!text-[#3d3d3d] [&_svg]:!size-6';

/** Plain readable copy when UILM keys are missing or still untranslated. */
const configText = (
  t: TFunction,
  key: string,
  fallback: string,
  options?: Record<string, unknown>
) => {
  const value = t(key, { defaultValue: fallback, ...options });
  if (!value || value === key || /^[A-Z][A-Z0-9_]+$/.test(String(value))) {
    return fallback;
  }
  return String(value);
};

type AgentConfigurationCardProps = {
  agentId: string;
  isAgentDetailsLoading?: boolean;
  sectionState: Record<string, boolean>;
  onSectionStateChange: (key: string, checked: boolean) => void;
  isLoadingLlmModels: boolean;
  availableChatModels: IGetAgentModelResponse;
  isLoadingCustomLlmModels: boolean;
  availableCustomChatModels: IGetCustomLlmModelsResponse['models'];
  currentLlmModel: string;
  currentLlmProvider: string;
  currentLlmModelId: string;
  isBlocksProvider: boolean;
  onModelChange: (value: {
    modelName: string;
    provider: string;
    isBlocksProvider: boolean;
    modelId?: string | null;
    temperature: number;
    maxTokens: number;
  }) => void;
  responseType: string;
  onResponseTypeChange: (value: string) => void;
  responseFormat: string | null;
  onResponseFormatChange: (value: string | null) => void;
  basePrompt: string;
  onBasePromptChange: (value: string) => void;
  autoBasePrompt: string;
  temperatureValue: number;
  onTemperatureChange: (value: number) => void;
  maxResponseValue: number;
  onMaxResponseChange: (value: number) => void;
  isModelParametersOpen: boolean;
  onModelParametersOpenChange: (open: boolean) => void;
  enableQueryEnhancement: boolean;
  onEnableQueryEnhancementChange: (value: boolean) => void;
  scoreThreshold: number;
  onScoreThresholdChange: (value: number) => void;
  topK: number;
  onTopKChange: (value: number) => void;
  denseWeight: number;
  onDenseWeightChange: (value: number) => void;
  projectKey: string;
  selectedFolderIds: string[];
  onSelectedFolderIdsChange: (value: string[]) => void;
  selectedToolIds: string[];
  onSelectedToolIdsChange: (value: string[]) => void;
  selectedSkillIds: string[];
  onSelectedSkillIdsChange: (value: string[]) => void;
  selectedSubagentIds: string[];
  onSelectedSubagentIdsChange: (value: string[]) => void;
  enableSummary: boolean;
  onEnableSummaryChange: (value: boolean) => void;
  lastNConversations: number;
  onLastNConversationsChange: (value: number) => void;
  agentName?: string;
  greeting: string;
  onGreetingChange: (value: string) => void;
  enableSmartSuggestions: boolean;
  onEnableSmartSuggestionsChange: (value: boolean) => void;
  enablePresetQuestions: boolean;
  onEnablePresetQuestionsChange: (value: boolean) => void;
  presetQuestions: string[];
  onPresetQuestionsChange: (questions: string[]) => void;
  guardrailOutputStrategy?: GuardrailOutputStrategy;
  onGuardrailOutputStrategyChange?: (value: GuardrailOutputStrategy) => void;
  configuredGuardrails?: Record<string, GuardrailConfig>;
  onConfiguredGuardrailsChange?: (value: Record<string, GuardrailConfig>) => void;
  guardrailErrors?: Record<string, string>;
  // Previous safety mechanism props preserved:
  enablePreValidation?: boolean;
  onEnablePreValidationChange?: (value: boolean) => void;
  enablePostValidation?: boolean;
  onEnablePostValidationChange?: (value: boolean) => void;
  enablePiiDetection?: boolean;
  onEnablePiiDetectionChange?: (value: boolean) => void;
  enableInjectionDetection?: boolean;
  onEnableInjectionDetectionChange?: (value: boolean) => void;
  inputRiskThreshold?: number;
  onInputRiskThresholdChange?: (value: number) => void;
  outputRiskThreshold?: number;
  onOutputRiskThresholdChange?: (value: number) => void;
  enableKeywordCheck?: boolean;
  onEnableKeywordCheckChange?: (value: boolean) => void;
  bannedKeywords?: string[];
  onBannedKeywordsChange?: (value: string[]) => void;
  bannedPatterns?: string[];
  onBannedPatternsChange?: (value: string[]) => void;
  enableCustomRules?: boolean;
  onEnableCustomRulesChange?: (value: boolean) => void;
  customRules?: CustomRule[];
  onCustomRulesChange?: (value: CustomRule[]) => void;
  agent?: Agent | null;
  /** @deprecated Prefer `agent` + myShareAccess; kept for call-site compatibility. */
  createdBy?: string | null;
};

export function AgentConfigurationCard({
  agentId,
  isAgentDetailsLoading = false,
  sectionState,
  onSectionStateChange,
  isLoadingLlmModels,
  availableChatModels,
  isLoadingCustomLlmModels,
  availableCustomChatModels,
  currentLlmModel,
  currentLlmProvider,
  currentLlmModelId,
  isBlocksProvider,
  onModelChange,
  responseType,
  onResponseTypeChange,
  responseFormat,
  onResponseFormatChange,
  basePrompt,
  onBasePromptChange,
  autoBasePrompt,
  temperatureValue,
  onTemperatureChange,
  maxResponseValue,
  onMaxResponseChange,
  isModelParametersOpen,
  onModelParametersOpenChange,
  enableQueryEnhancement,
  onEnableQueryEnhancementChange,
  scoreThreshold,
  onScoreThresholdChange,
  topK,
  onTopKChange,
  denseWeight,
  onDenseWeightChange,
  projectKey,
  selectedFolderIds,
  onSelectedFolderIdsChange,
  selectedToolIds,
  onSelectedToolIdsChange,
  selectedSkillIds,
  onSelectedSkillIdsChange,
  selectedSubagentIds,
  onSelectedSubagentIdsChange,
  enableSummary,
  onEnableSummaryChange,
  lastNConversations,
  onLastNConversationsChange,
  agentName,
  greeting,
  onGreetingChange,
  enableSmartSuggestions,
  onEnableSmartSuggestionsChange,
  enablePresetQuestions,
  onEnablePresetQuestionsChange,
  presetQuestions,
  onPresetQuestionsChange,
  guardrailOutputStrategy = 'block',
  onGuardrailOutputStrategyChange,
  configuredGuardrails = {},
  onConfiguredGuardrailsChange,
  guardrailErrors,
  enablePreValidation,
  onEnablePreValidationChange,
  enablePostValidation,
  onEnablePostValidationChange,
  enablePiiDetection,
  onEnablePiiDetectionChange,
  enableInjectionDetection,
  onEnableInjectionDetectionChange,
  inputRiskThreshold,
  onInputRiskThresholdChange,
  outputRiskThreshold,
  onOutputRiskThresholdChange,
  enableKeywordCheck,
  onEnableKeywordCheckChange,
  bannedKeywords,
  onBannedKeywordsChange,
  bannedPatterns,
  onBannedPatternsChange,
  enableCustomRules,
  onEnableCustomRulesChange,
  customRules,
  onCustomRulesChange,
  agent,
  createdBy,
}: AgentConfigurationCardProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const userId = useCommonStore((state) => state.user?.id);
  const shareAgent = agent ?? (createdBy ? { created_by: createdBy } : null);
  const isOwner = isAgentOwnerAccess(shareAgent, userId);
  const canEdit = canEditAgentConfig(shareAgent, userId);
  const manageTools = canManageTools(shareAgent, userId);
  const manageSkills = canManageSkills(shareAgent, userId);
  const manageSubagents = canManageSubagents(shareAgent, userId);
  const manageKb = canManageKb(shareAgent, userId);
  const isOwnershipPending = isAgentDetailsLoading || !shareAgent || userId == null;

  const canManageSection = (key: string) => {
    if (key === 'tools') return manageTools;
    if (key === 'skills') return manageSkills;
    if (key === 'subagents') return manageSubagents;
    if (key === 'knowledge-base') return manageKb;
    if (key === 'welcome-guide') return isOwner;
    // LLM / guardrails / memory follow canEditConfig
    return canEdit;
  };

  const configSubtitle = isOwnershipPending
    ? configText(t, 'CREATE_YOUR_OWN_UNIQUE_SETUP', 'Create your own unique setup')
    : !canEdit
      ? configText(
        t,
        'YOU_ARE_NOT_THE_OWNER_OF_THIS_AGENT',
        'You are not the owner of this agent.'
      )
      : !isOwner
        ? configText(t, 'SHARED_WITH_YOU_CAN_EDIT', 'Shared with you · You can edit')
        : configText(t, 'CREATE_YOUR_OWN_UNIQUE_SETUP', 'Create your own unique setup');
  const [isAddKnowledgeBaseModalOpen, setIsAddKnowledgeBaseModalOpen] = useState(false);
  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);
  const [isAddSkillModalOpen, setIsAddSkillModalOpen] = useState(false);
  const [isAddSubagentModalOpen, setIsAddSubagentModalOpen] = useState(false);
  const [isAddGuardrailModalOpen, setIsAddGuardrailModalOpen] = useState(false);

  const handleAddGuardrail = (item: GuardrailCatalogItem) => {
    if (configuredGuardrails && configuredGuardrails[item.id]) {
      return;
    }
    const updated = {
      ...configuredGuardrails,
      [item.id]: {
        enabled: true,
        ...(item.params?.keywords ? { keywords: [] } : {}),
      },
    };
    onConfiguredGuardrailsChange?.(updated);
    onSectionStateChange('guardrails', true);
  };

  const handleRemoveGuardrail = (guardrailId: string) => {
    const updated = { ...configuredGuardrails };
    delete updated[guardrailId];
    onConfiguredGuardrailsChange?.(updated);
    if (Object.keys(updated).length === 0) {
      onSectionStateChange('guardrails', false);
    }
  };

  const handleAddKnowledgeBase = (id: string) => {
    if (selectedFolderIds.includes(id)) {
      return;
    }

    onSelectedFolderIdsChange([...selectedFolderIds, id]);
  };

  const handleRemoveKnowledgeBase = (id: string) => {
    onSelectedFolderIdsChange(selectedFolderIds.filter((folderId) => folderId !== id));
  };

  const handleAddTool = (tool: ITool) => {
    if (selectedToolIds.includes(tool.id)) {
      return;
    }

    onSelectedToolIdsChange([...selectedToolIds, tool.id]);
    onSectionStateChange('tools', true);
  };

  const handleRemoveTool = (toolId: string) => {
    const nextIds = selectedToolIds.filter((id) => id !== toolId);
    onSelectedToolIdsChange(nextIds);
  };

  const handleAddSkill = (skill: SkillListItem) => {
    if (selectedSkillIds.includes(skill.id)) {
      return;
    }

    onSelectedSkillIdsChange([...selectedSkillIds, skill.id]);
    onSectionStateChange('skills', true);
  };

  const handleRemoveSkill = (skillId: string) => {
    const nextIds = selectedSkillIds.filter((id) => id !== skillId);
    onSelectedSkillIdsChange(nextIds);
  };

  const handleAddSubagent = (subagent: AgentSummary) => {
    if (selectedSubagentIds.includes(subagent.id)) {
      return;
    }

    onSelectedSubagentIdsChange([...selectedSubagentIds, subagent.id]);
    onSectionStateChange('subagents', true);
  };

  const handleRemoveSubagent = (subagentId: string) => {
    const nextIds = selectedSubagentIds.filter((id) => id !== subagentId);
    onSelectedSubagentIdsChange(nextIds);
  };

  return (
    <div
      data-testid="agent-configuration-card"
      className={cn(
        'flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-3xl border shadow-sm',
        ui.dialog,
        isAgentDetailsLoading && 'pointer-events-none opacity-60',
      )}
    >
      <div className={cn('shrink-0 px-6 pb-4 pt-6', ui.header)}>
        <h2 className={cn('text-[1.65rem] font-bold leading-tight tracking-tight', ui.title)}>
          {configText(t, 'CONFIGURATION', 'Configuration')}
        </h2>
        <p className={cn('mt-2 text-sm leading-relaxed', ui.subtitle)}>{configSubtitle}</p>
      </div>
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-5',
          '[scrollbar-width:thin] [scrollbar-color:#d1d1d1_transparent]',
          '[&::-webkit-scrollbar]:w-[7px] [&::-webkit-scrollbar-track]:bg-transparent',
          '[&::-webkit-scrollbar-thumb]:min-h-[48px] [&::-webkit-scrollbar-thumb]:rounded-[100px] [&::-webkit-scrollbar-thumb]:bg-[#d1d1d1]',
          'dark:[scrollbar-color:#d1d1d1_transparent] dark:[&::-webkit-scrollbar-thumb]:bg-[#d1d1d1]',
        )}
      >
        <div className="flex w-full flex-col items-start gap-6 pb-2">
          {CONFIG_SECTIONS.map((section) => {
            const isComingSoon = !!section.comingSoon;
            const sectionTitle = configText(
              t,
              section.titleKey,
              section.titleFallback || section.titleKey
            );

            const sectionContent =
              section.key === 'llm' ? (
                <LlmConfigurationSection
                  isLoadingLlmModels={isLoadingLlmModels}
                  availableChatModels={availableChatModels}
                  isLoadingCustomLlmModels={isLoadingCustomLlmModels}
                  availableCustomChatModels={availableCustomChatModels}
                  currentLlmModel={currentLlmModel}
                  currentLlmProvider={currentLlmProvider}
                  currentLlmModelId={currentLlmModelId}
                  isBlocksProvider={isBlocksProvider}
                  onModelChange={onModelChange}
                  responseType={responseType}
                  onResponseTypeChange={onResponseTypeChange}
                  responseFormat={responseFormat}
                  onResponseFormatChange={onResponseFormatChange}
                  basePrompt={basePrompt}
                  onBasePromptChange={onBasePromptChange}
                  autoBasePrompt={autoBasePrompt}
                  temperatureValue={temperatureValue}
                  onTemperatureChange={onTemperatureChange}
                  maxResponseValue={maxResponseValue}
                  onMaxResponseChange={onMaxResponseChange}
                  isModelParametersOpen={isModelParametersOpen}
                  onModelParametersOpenChange={onModelParametersOpenChange}
                  agentName={agentName}
                  selectedToolIds={selectedToolIds}
                  disabled={!canEdit}
                />
              ) : section.key === 'knowledge-base' ? (
                <KnowledgeBaseSection
                  enabled={manageKb && !!sectionState[section.key]}
                  canEdit={manageKb}
                  projectKey={projectKey}
                  selectedFolderIds={selectedFolderIds}
                  onSelectedFolderIdsChange={onSelectedFolderIdsChange}
                  enableQueryEnhancement={enableQueryEnhancement}
                  onEnableQueryEnhancementChange={onEnableQueryEnhancementChange}
                  scoreThreshold={scoreThreshold}
                  onScoreThresholdChange={onScoreThresholdChange}
                  topK={topK}
                  onTopKChange={onTopKChange}
                  denseWeight={denseWeight}
                  onDenseWeightChange={onDenseWeightChange}
                />
              ) : section.key === 'tools' ? (
                <ToolsSection
                  enabled={manageTools && !!sectionState[section.key]}
                  canEdit={manageTools}
                  selectedToolIds={selectedToolIds}
                  attachedTools={agent?.tools}
                  onRemoveTool={handleRemoveTool}
                />
              ) : section.key === 'skills' ? (
                <SkillsSection
                  enabled={manageSkills && !!sectionState[section.key]}
                  canEdit={manageSkills}
                  selectedSkillIds={selectedSkillIds}
                  onRemoveSkill={handleRemoveSkill}
                />
              ) : section.key === 'subagents' ? (
                <SubagentsSection
                  enabled={manageSubagents && !!sectionState[section.key]}
                  canEdit={manageSubagents}
                  projectKey={projectKey}
                  selectedSubagentIds={selectedSubagentIds}
                  onRemoveSubagent={handleRemoveSubagent}
                />
              ) : section.key === 'memory' ? (
                <MemorySection
                  enableSummary={enableSummary}
                  onEnableSummaryChange={onEnableSummaryChange}
                  lastNConversations={lastNConversations}
                  onLastNConversationsChange={onLastNConversationsChange}
                  disabled={!canEdit}
                />
              ) : section.key === 'welcome-guide' ? (
                <WelcomeGuideSection
                  agentName={agentName}
                  greeting={greeting}
                  onGreetingChange={onGreetingChange}
                  enableSmartSuggestions={enableSmartSuggestions}
                  onEnableSmartSuggestionsChange={onEnableSmartSuggestionsChange}
                  enablePresetQuestions={enablePresetQuestions}
                  onEnablePresetQuestionsChange={onEnablePresetQuestionsChange}
                  presetQuestions={presetQuestions}
                  onPresetQuestionsChange={onPresetQuestionsChange}
                  disabled={!isOwner}
                />
              ) : section.key === 'guardrails' ? (
                <GuardrailsSection
                  enabled={canEdit && !!sectionState[section.key]}
                  canEdit={canEdit}
                  outputStrategy={guardrailOutputStrategy}
                  onOutputStrategyChange={onGuardrailOutputStrategyChange}
                  guardrails={configuredGuardrails}
                  onGuardrailsChange={onConfiguredGuardrailsChange}
                  errors={guardrailErrors}
                />
                /*
                <SafetyMechanismSection
                  enabled={canEdit && !!sectionState[section.key]}
                  enablePreValidation={enablePreValidation}
                  onEnablePreValidationChange={onEnablePreValidationChange}
                  enablePostValidation={enablePostValidation}
                  onEnablePostValidationChange={onEnablePostValidationChange}
                  enablePiiDetection={enablePiiDetection}
                  onEnablePiiDetectionChange={onEnablePiiDetectionChange}
                  enableInjectionDetection={enableInjectionDetection}
                  onEnableInjectionDetectionChange={onEnableInjectionDetectionChange}
                  inputRiskThreshold={inputRiskThreshold}
                  onInputRiskThresholdChange={onInputRiskThresholdChange}
                  outputRiskThreshold={outputRiskThreshold}
                  onOutputRiskThresholdChange={onOutputRiskThresholdChange}
                  enableKeywordCheck={enableKeywordCheck}
                  onEnableKeywordCheckChange={onEnableKeywordCheckChange}
                  bannedKeywords={bannedKeywords}
                  onBannedKeywordsChange={onBannedKeywordsChange}
                  bannedPatterns={bannedPatterns}
                  onBannedPatternsChange={onBannedPatternsChange}
                  enableCustomRules={enableCustomRules}
                  onEnableCustomRulesChange={onEnableCustomRulesChange}
                  customRules={customRules}
                  onCustomRulesChange={onCustomRulesChange}
                />
                */
              ) : (
                <div className="rounded-lg border border-[#e7e7e7] bg-[#f6f6f6] px-5 py-4 text-sm text-[#6d6d6d] dark:border-border dark:bg-muted/30 dark:text-muted-foreground">
                  {section.contentKey
                    ? configText(
                      t,
                      section.contentKey,
                      section.contentFallback || section.contentKey
                    )
                    : null}
                </div>
              );

            return (
              <div
                key={section.key}
                className="flex w-full flex-col items-start gap-4 border-b border-[#e7e7e7] pb-6 last:border-b-0 dark:border-border"
              >
                <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex min-w-0 grow basis-48 items-center gap-x-2.5 gap-y-1">
                    <p className="truncate text-[18px] font-semibold leading-[1.5] text-[#3d3d3d] dark:text-true-white">
                      {sectionTitle}
                    </p>
                    {section.badgeLabelKey ? (
                      <span className="shrink-0 rounded-full bg-[#f6f6f6] px-2.5 py-0.5 text-[11px] font-medium text-[#6d6d6d] dark:bg-muted dark:text-muted-foreground">
                        {configText(
                          t,
                          section.badgeLabelKey,
                          section.badgeLabelFallback || section.badgeLabelKey
                        )}
                      </span>
                    ) : null}
                  </div>

                  {canManageSection(section.key) && (section.hasAdd || section.hasToggle) ? (
                    <div className="ml-auto flex shrink-0 items-center gap-3">
                      {section.hasAdd ? (
                        <Button
                          type="button"
                          variant="outline"
                          className={figmaAddButtonClassName}
                          onClick={() => {
                            if (section.key === 'knowledge-base') {
                              setIsAddKnowledgeBaseModalOpen(true);
                            }
                            if (section.key === 'tools') {
                              setIsAddToolModalOpen(true);
                            }
                            if (section.key === 'skills') {
                              setIsAddSkillModalOpen(true);
                            }
                            if (section.key === 'subagents') {
                              setIsAddSubagentModalOpen(true);
                            }
                            if (section.key === 'guardrails') {
                              setIsAddGuardrailModalOpen(true);
                            }
                          }}
                          disabled={
                            isAgentDetailsLoading ||
                            !sectionState[section.key]
                          }
                        >
                          <Plus className="size-6" strokeWidth={1.5} aria-hidden />
                          {configText(t, 'ADD', 'Add')}
                        </Button>
                      ) : null}

                      {section.hasToggle ? (
                        <Switch
                          checked={isComingSoon ? false : !!sectionState[section.key]}
                          disabled={
                            isAgentDetailsLoading ||
                            isComingSoon ||
                            !canManageSection(section.key)
                          }
                          className={figmaConfigSwitchClassName}
                          onCheckedChange={(checked) => {
                            if (isComingSoon || !canManageSection(section.key)) return;
                            onSectionStateChange(section.key, checked);
                          }}
                          aria-label={
                            isComingSoon
                              ? `${sectionTitle}: ${configText(t, 'COMING_SOON', 'Coming Soon')}`
                              : sectionTitle
                          }
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="w-full">{sectionContent}</div>
              </div>
            );
          })}
        </div>
      </div>

      <AgentConfigAddKBModal
        open={isAddKnowledgeBaseModalOpen}
        onOpenChange={setIsAddKnowledgeBaseModalOpen}
        projectKey={projectKey}
        currentFolderIds={selectedFolderIds}
        onAddKB={handleAddKnowledgeBase}
        onRemoveKB={handleRemoveKnowledgeBase}
      />

      <AgentConfigAddToolModal
        open={isAddToolModalOpen}
        onOpenChange={setIsAddToolModalOpen}
        selectedToolIds={selectedToolIds}
        onAddTool={handleAddTool}
        onRemoveTool={handleRemoveTool}
      />

      <AgentConfigAddSkillModal
        open={isAddSkillModalOpen}
        onOpenChange={setIsAddSkillModalOpen}
        selectedSkillIds={selectedSkillIds}
        onAddSkill={handleAddSkill}
        onRemoveSkill={handleRemoveSkill}
      />

      <AgentConfigAddSubagentModal
        open={isAddSubagentModalOpen}
        onOpenChange={setIsAddSubagentModalOpen}
        agentId={agentId}
        projectKey={projectKey}
        selectedSubagentIds={selectedSubagentIds}
        onAddSubagent={handleAddSubagent}
        onRemoveSubagent={handleRemoveSubagent}
      />

      <AgentConfigAddGuardrailModal
        open={isAddGuardrailModalOpen}
        onOpenChange={setIsAddGuardrailModalOpen}
        configuredGuardrailIds={Object.keys(configuredGuardrails)}
        onAddGuardrail={handleAddGuardrail}
        onRemoveGuardrail={handleRemoveGuardrail}
      />
    </div>
  );
}